import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
// Only its type here: the client itself is imported with the first upload (sendWithTus).
import type { Upload } from 'tus-js-client';
import { filesApi } from '../../api/endpoints';
import { isApiError } from '../../api/http';
import type { UploadIntent } from '../../api/types';
import { useI18n } from '../../i18n';
import { errorMessage, Icon, ProgressBar, useToast } from '../../ui';
import { copyName, rememberChoice, takenBy, type ConflictChoice } from './name-conflict';
import { NameConflictDialog, type NameConflictAnswer, type NameConflictQuestion } from './NameConflictDialog';
import { acceptOf, sortByPolicy, storageQuery, uploadBlock, useStorage } from './storage';

// Uploads: announce the file (the files service reserves quota and returns a one-time token),
// send the bytes with tus straight to the upload service (resumable, in chunks), then wait for
// the virus scan. One at a time, so a big batch does not saturate the connection. A name already
// taken where it goes pauses the upload until its person says what to do (TF-105).

export type UploadStatus = 'queued' | 'conflict' | 'uploading' | 'scanning' | 'done' | 'blocked' | 'failed';

export interface UploadItem {
  id: string;
  name: string;
  tenantId: string;
  folderId: string | null;
  /** Upload a new version of this file instead of a new file. */
  versionOf: string | null;
  fileId: string | null;
  progress: number;
  status: UploadStatus;
  error: string | null;
  /** Who asked for it, so they can show its progress where they are (TF-19: "crm:deal:<id>"). */
  tag: string | null;
  /** The files queued together, for "apply to all" when several names are taken (TF-105). */
  batch: string;
  /** What became of a taken name: "used the file already there", "added as a copy"... (TF-105). */
  note: string | null;
}

/**
 * Where an upload goes, and what the one who started it wants to do along the way. onAnnounced runs
 * once the file exists (it has an id, its bytes not sent yet) - where a record links it at once, so
 * the link survives the page being left; onSettled runs when it is done, blocked by the virus scan
 * or failed.
 */
export interface UploadTarget {
  tenantId: string;
  folderId: string | null;
  /** Upload a new version of this file instead of a new file. */
  versionOf?: string;
  tag?: string;
  onAnnounced?: (fileId: string) => Promise<void> | void;
  onSettled?: (status: 'done' | 'blocked' | 'failed', fileId: string | null) => void;
}

interface UploadApi {
  items: UploadItem[];
  enqueue: (files: File[], target: UploadTarget) => void;
  clearFinished: () => void;
  /**
   * Stops the uploads with this tag whose bytes are not all sent (queued or uploading, TF-106): a file
   * already announced goes to the trash. Those being scanned are sent, so they stay. Returns how many.
   */
  cancel: (tag: string) => number;
  /** Moves uploads to another tag: a new task's uploads become the task's once it exists (TF-106). */
  retag: (from: string, to: string) => void;
}

const UploadContext = createContext<UploadApi | null>(null);

async function sendWithTus(
  file: File,
  intent: UploadIntent,
  onProgress: (percent: number) => void,
  // The running transfer, and a way to end the wait for it: tus's abort settles neither callback.
  onStart: (upload: Upload, stop: (error: Error) => void) => void,
): Promise<void> {
  // Most visits upload nothing: the tus client comes with the first upload, not with the app.
  const { Upload } = await import('./tus');
  return new Promise((resolve, reject) => {
    const upload = new Upload(file, {
      endpoint: new URL(intent.endpoint, location.origin).toString(),
      metadata: { token: intent.token },
      chunkSize: 16 * 1024 * 1024,
      retryDelays: [0, 1000, 3000, 5000, 10000],
      storeFingerprintForResuming: false,
      removeFingerprintOnSuccess: true,
      onProgress: (sent, total) => onProgress(total > 0 ? Math.round((sent / total) * 100) : 100),
      onError: reject,
      onSuccess: () => resolve(),
    });
    onStart(upload, reject);
    upload.start();
  });
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function UploadProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<UploadItem[]>([]);
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const chain = useRef<Promise<void>>(Promise.resolve());
  const nextId = useRef(1);
  // The callbacks stay out of state: they are for this session's code, not for rendering.
  const hooks = useRef(new Map<string, Pick<UploadTarget, 'onAnnounced' | 'onSettled'>>());
  // Stopping (TF-106): which uploads were asked to stop, and the transfers running now.
  const cancelled = useRef(new Set<string>());
  const sending = useRef(new Map<string, { upload: Upload; stop: (error: Error) => void }>());
  // A taken name (TF-105): the question on screen, how it is answered, and a batch's "apply to all".
  const [question, setQuestion] = useState<(NameConflictQuestion & { itemId: string }) | null>(null);
  const answers = useRef(new Map<string, (answer: NameConflictAnswer | null) => void>());
  const batchChoices = useRef(new Map<string, ConflictChoice>());
  const batchLeft = useRef(new Map<string, number>());

  const update = useCallback((id: string, patch: Partial<UploadItem>) => {
    setItems((current) => current.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }, []);

  const run = useCallback(
    async (item: UploadItem, file: File) => {
      const { tenantId } = item;
      const { onAnnounced, onSettled } = hooks.current.get(item.id) ?? {};
      hooks.current.delete(item.id);
      let announcedId: string | null = null;
      // A version of a file that was already there: a failure must not take that file away from the
      // record it is linked to (callers unlink the file of a failed upload), nor put it in the trash.
      let intoExisting = false;
      const settle = (status: 'done' | 'blocked' | 'failed') => {
        update(item.id, { status });
        onSettled?.(status, intoExisting && status !== 'done' ? null : announcedId);
      };
      batchLeft.current.set(item.batch, Math.max(0, (batchLeft.current.get(item.batch) ?? 1) - 1));
      const refresh = () => {
        void queryClient.invalidateQueries({ queryKey: ['items', tenantId] });
        void queryClient.invalidateQueries({ queryKey: ['file', tenantId] });
        void queryClient.invalidateQueries({ queryKey: ['usage', tenantId] });
      };
      const stopped = () => cancelled.current.has(item.id);
      try {
        if (stopped()) throw new Error(t('upload.cancelled'));
        update(item.id, { status: 'uploading' });
        const contentType = file.type || undefined;
        // Set on every path below that goes on to send bytes (the others return or throw).
        let fileId!: string;
        let intent!: UploadIntent;
        if (item.versionOf) {
          intent = (await filesApi.announceVersion(tenantId, item.versionOf, { sizeBytes: file.size, contentType })).upload;
          fileId = item.versionOf;
          intoExisting = true;
        } else {
          // The name is checked here, before any byte is sent (TF-105): a taken one is asked about.
          let name = file.name;
          let copying = false;
          for (let attempt = 0; ; attempt++) {
            try {
              const announced = await filesApi.announceFile(tenantId, { name, folderId: item.folderId, sizeBytes: file.size, contentType });
              intent = announced.upload;
              fileId = announced.file.id;
              if (copying) update(item.id, { name, note: t('upload.conflict.addedAsCopy') });
              break;
            } catch (error) {
              const existing = takenBy(error);
              if (!existing || (copying && attempt >= 3)) throw error;
              if (copying) {
                name = copyName(file.name, new Date(), attempt);
                continue;
              }
              const choice = batchChoices.current.get(item.batch) ?? (await ask(item, file, existing, onAnnounced !== undefined));
              if (!choice) throw new Error(t('upload.cancelled'));
              if (choice === 'existing') {
                // Nothing is sent: the file already there is linked where the upload was started.
                try {
                  await onAnnounced?.(existing.id);
                } catch {
                  /* reported by the caller */
                }
                update(item.id, { fileId: existing.id, status: 'done', progress: 100, note: t('upload.conflict.usedExisting') });
                onSettled?.('done', existing.id);
                refresh();
                return;
              }
              if (choice === 'version') {
                intent = (await filesApi.announceVersion(tenantId, existing.id, { sizeBytes: file.size, contentType })).upload;
                fileId = existing.id;
                intoExisting = true;
                update(item.id, { note: t('upload.conflict.addedAsVersion') });
                break;
              }
              copying = true;
              name = copyName(file.name, new Date());
            }
          }
        }
        update(item.id, { fileId, status: 'uploading' });
        announcedId = fileId;
        refresh();
        // The caller's own step (linking it to a record) must not stop the upload: it says so itself.
        try {
          await onAnnounced?.(fileId);
        } catch {
          /* reported by the caller */
        }
        if (stopped()) throw new Error(t('upload.cancelled'));
        await sendWithTus(
          file,
          intent,
          (progress) => update(item.id, { progress }),
          (upload, stop) => sending.current.set(item.id, { upload, stop }),
        );
        sending.current.delete(item.id);
        if (stopped()) throw new Error(t('upload.cancelled'));
        update(item.id, { status: 'scanning', progress: 100 });
        refresh();
        // The virus scan decides whether the file becomes available.
        for (let attempt = 0; attempt < 150; attempt++) {
          await sleep(attempt < 10 ? 1000 : 2000);
          const details = await filesApi.file(tenantId, fileId);
          const latest = [...details.versions].sort((a, b) => b.versionNo - a.versionNo)[0];
          if (latest?.state === 'clean') return settle('done'), refresh();
          if (latest?.state === 'infected') return settle('blocked'), refresh();
          if (latest?.state === 'failed') throw new Error(t('files.state.failed'));
        }
      } catch (error) {
        sending.current.delete(item.id);
        // Stopped by the one who started it: a file already announced goes to the trash, not the drive.
        if (stopped()) {
          cancelled.current.delete(item.id);
          if (announcedId && !intoExisting) await filesApi.trashFile(tenantId, announcedId).catch(() => undefined);
          update(item.id, { error: t('upload.cancelled') });
          settle('failed');
          refresh();
          return;
        }
        // Whatever the reason, the storage figures may have changed: the banner follows at once.
        void queryClient.invalidateQueries({ queryKey: ['usage', tenantId] });
        const reason = isApiError(error, 413) && error.code === 'FILE_TOO_LARGE'
          ? t('upload.tooLargeServer')
          : isApiError(error, 415)
            ? t('upload.typeNotAllowedServer')
          : isApiError(error, 413)
          ? t('upload.quota')
          : isApiError(error, 503)
            ? t('storage.uploadUnavailable')
          : isApiError(error, 409)
            ? t('upload.exists')
            : error instanceof Error && !isApiError(error)
              ? error.message
              : errorMessage(error, t('common.somethingWrong'));
        update(item.id, { error: reason });
        settle('failed');
        refresh();
      }
    },
    // ask (below) uses refs and state setters only, so the first render's is as good as any.
    [queryClient, t, update],
  );

  /** Pauses an upload on a taken name until its person answers (TF-105); null is Cancel. */
  function ask(item: UploadItem, file: File, existing: NonNullable<ReturnType<typeof takenBy>>, canLink: boolean): Promise<ConflictChoice | null> {
    update(item.id, { status: 'conflict' });
    return new Promise((resolve) => {
      answers.current.set(item.id, (answer) => {
        answers.current.delete(item.id);
        setQuestion(null);
        if (answer) {
          rememberChoice(answer.choice);
          if (answer.applyToAll) batchChoices.current.set(item.batch, answer.choice);
          update(item.id, { status: 'uploading' });
        }
        resolve(answer?.choice ?? null);
      });
      setQuestion({ itemId: item.id, tenantId: item.tenantId, file: { name: file.name, size: file.size }, existing, canLink, remaining: batchLeft.current.get(item.batch) ?? 0 });
    });
  }

  const enqueue = useCallback<UploadApi['enqueue']>(
    (files, target) => {
      const batch = String(nextId.current);
      batchLeft.current.set(batch, files.length);
      for (const file of files) {
        const item: UploadItem = {
          id: String(nextId.current++),
          name: file.name,
          tenantId: target.tenantId,
          folderId: target.folderId,
          versionOf: target.versionOf ?? null,
          fileId: null,
          progress: 0,
          status: 'queued',
          error: null,
          tag: target.tag ?? null,
          batch,
          note: null,
        };
        hooks.current.set(item.id, { onAnnounced: target.onAnnounced, onSettled: target.onSettled });
        setItems((current) => [...current, item]);
        chain.current = chain.current.then(() => run(item, file));
      }
    },
    [run],
  );

  const clearFinished = useCallback(() => setItems((current) => current.filter((item) => !['done', 'blocked', 'failed'].includes(item.status))), []);

  // Read in the callbacks below without making them change with every progress tick.
  const latest = useRef(items);
  latest.current = items;
  const cancel = useCallback((tag: string) => {
    const running = latest.current.filter((item) => item.tag === tag && (item.status === 'queued' || item.status === 'uploading' || item.status === 'conflict'));
    for (const item of running) {
      cancelled.current.add(item.id);
      // One waiting for an answer about its name: answered "no" (TF-105).
      answers.current.get(item.id)?.(null);
      // Stops the transfer, tells the upload service to drop what it has, and ends the wait for it.
      const transfer = sending.current.get(item.id);
      if (transfer) {
        void transfer.upload.abort(true).catch(() => undefined);
        transfer.stop(new Error(t('upload.cancelled')));
      }
    }
    return running.length;
  }, [t]);
  const retag = useCallback((from: string, to: string) => setItems((current) => current.map((item) => (item.tag === from ? { ...item, tag: to } : item))), []);

  // A batch that went through clears itself after a moment; anything that went wrong stays until dismissed.
  useEffect(() => {
    if (items.length === 0 || !items.every((item) => item.status === 'done')) return;
    const timer = window.setTimeout(clearFinished, 6000);
    return () => window.clearTimeout(timer);
  }, [items, clearFinished]);
  const value = useMemo(() => ({ items, enqueue, clearFinished, cancel, retag }), [items, enqueue, clearFinished, cancel, retag]);
  return (
    <UploadContext.Provider value={value}>
      {children}
      {question && <NameConflictDialog key={question.itemId} question={question} onAnswer={(answer) => answers.current.get(question.itemId)?.(answer)} />}
    </UploadContext.Provider>
  );
}

export function useUploads(): UploadApi {
  const api = useContext(UploadContext);
  if (!api) throw new Error('useUploads outside UploadProvider');
  return api;
}

/** Where an upload stands, in words: in the panel, and wherever else one is shown. */
export function uploadLabel(item: UploadItem, t: ReturnType<typeof useI18n>['t']): string {
  switch (item.status) {
    case 'queued':
      return t('upload.queued');
    case 'conflict':
      return t('upload.conflict.waiting');
    case 'uploading':
      return t('upload.uploading', { percent: item.progress });
    case 'scanning':
      return t('upload.scanning');
    case 'done':
      return item.note ? `${t('upload.done')}: ${item.note}` : t('upload.done');
    case 'blocked':
      return t('upload.blocked');
    case 'failed':
      return t('upload.failed', { reason: item.error ?? '' });
  }
}

/**
 * Starting uploads anywhere (the drive, a record's Files, TF-19): whether uploading is possible at
 * all, what the file picker should offer, and a start that says - once per reason - what the plan
 * does not allow and queues only the rest, so nothing is sent that would be refused.
 */
export function useUploadStarter(tenantId: string) {
  const { t, bytes } = useI18n();
  const toast = useToast();
  const uploads = useUploads();
  const queryClient = useQueryClient();
  const storage = useStorage(tenantId);
  const reasonOf = (block: ReturnType<typeof uploadBlock>) => (block === 'full' ? t('storage.uploadFull') : block === 'unavailable' ? t('storage.uploadUnavailable') : undefined);
  const blocked = uploadBlock(storage.data);
  const blockedReason = reasonOf(blocked);
  const start = async (list: FileList | File[] | null, target: Omit<UploadTarget, 'tenantId'>): Promise<number> => {
    // Taken at once: the caller may clear its file input right after.
    const chosen = Array.from(list ?? []);
    // Chosen before the company's limits arrived (a slow first answer): wait for them, so the check
    // below always runs. If they cannot be had, the files service still checks.
    const usage = storage.data ?? (await queryClient.fetchQuery(storageQuery(tenantId)).catch(() => undefined));
    const block = uploadBlock(usage);
    if (block) {
      toast.error(reasonOf(block)!);
      return 0;
    }
    const limits = usage?.files;
    const { allowed, wrongType, tooLarge } = sortByPolicy(chosen, limits);
    const names = (files: File[]) => files.map((file) => file.name).join(', ');
    if (limits && wrongType.length > 0) toast.error(t('upload.typeNotAllowed', { names: names(wrongType), types: limits.types.join(', ') }));
    if (limits && tooLarge.length > 0) toast.error(t('upload.tooLarge', { names: names(tooLarge), max: bytes(limits.maxBytes) }));
    if (allowed.length > 0) uploads.enqueue(allowed, { ...target, tenantId });
    return allowed.length;
  };
  return { start, blocked, blockedReason, accept: acceptOf(storage.data?.files) };
}

export function UploadPanel() {
  const { items, clearFinished } = useUploads();
  const { t } = useI18n();
  if (items.length === 0) return null;
  const allFinished = items.every((item) => ['done', 'blocked', 'failed'].includes(item.status));
  const label = (item: UploadItem) => uploadLabel(item, t);
  return (
    <section className="uploads" aria-labelledby="uploads-title">
      <div className="uploads-head">
        <h2 id="uploads-title">{t('upload.title')}</h2>
        {allFinished && (
          <button type="button" className="icon-btn" aria-label={t('upload.clear')} onClick={clearFinished}>
            <Icon name="close" />
          </button>
        )}
      </div>
      <ul aria-live="polite">
        {items.map((item) => (
          <li key={item.id}>
            <div className="upload-name">
              <strong title={item.name}>{item.name}</strong>
              <span className="small muted inline-row">
                {item.status === 'done' && <Icon name="check_circle" size={20} className="text-success" />}
                {item.status === 'blocked' && <Icon name="gpp_maybe" size={20} className="text-danger" />}
                {item.status === 'failed' && <Icon name="cancel" size={20} className="text-danger" />}
                {label(item)}
              </span>
            </div>
            {(item.status === 'uploading' || item.status === 'scanning') && (
              <ProgressBar value={item.progress} label={item.name} />
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The folder this person last uploaded into from outside the drive (a record's Files, TF-19),
 * remembered per company in this browser - a convenience, so it may be forgotten at any time and
 * then uploads go to the drive's top level.
 */
export function useUploadFolder(tenantId: string): [string | null, (folderId: string | null) => void] {
  const key = `teamfolio.uploadFolder.${tenantId}`;
  const [folderId, setFolderId] = useState<string | null>(() => {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  });
  const remember = useCallback(
    (next: string | null) => {
      setFolderId(next);
      try {
        if (next) window.localStorage.setItem(key, next);
        else window.localStorage.removeItem(key);
      } catch {
        /* storage unavailable: remembered for this page only */
      }
    },
    [key],
  );
  return [folderId, remember];
}
